import React from 'react'
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import InfoIcon from '@mui/icons-material/Info'
import WarningIcon from '@mui/icons-material/Warning'
import StarIcon from '@mui/icons-material/Star'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import Markdown from 'react-markdown'
import { CalloutWidgetData } from '../../../types/gotypes'

const ICON_MAP: Record<string, React.ElementType> = {
  lightbulb: LightbulbIcon,
  info: InfoIcon,
  warning: WarningIcon,
  star: StarIcon,
  quote: FormatQuoteIcon,
}

const STYLE_COLORS: Record<string, { border: string; bg: string; iconColor: string }> = {
  blue: { border: '#1976d2', bg: '#e3f2fd', iconColor: '#1565c0' },
  green: { border: '#2e7d32', bg: '#e8f5e9', iconColor: '#1b5e20' },
  amber: { border: '#f57c00', bg: '#fff3e0', iconColor: '#e65100' },
  neutral: { border: '#757575', bg: '#f5f5f5', iconColor: '#616161' },
}

interface CalloutWidgetRendererProps {
  data: CalloutWidgetData
}

export const CalloutWidgetRenderer: React.FC<CalloutWidgetRendererProps> = ({ data }) => {
  const style = STYLE_COLORS[data.style || 'blue'] || STYLE_COLORS.blue
  const IconComp = ICON_MAP[data.icon || 'info'] || InfoIcon

  return (
    <Box
      sx={{
        borderLeft: `4px solid ${style.border}`,
        backgroundColor: style.bg,
        borderRadius: 1,
        p: 2.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: data.body ? 1 : 0 }}>
        <IconComp sx={{ color: style.iconColor, fontSize: 22 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {data.title}
        </Typography>
      </Box>
      {data.body && (
        <Box
          sx={{
            pl: 4,
            '& p': { margin: '0.25em 0' },
            '& p:first-of-type': { mt: 0 },
            '& p:last-of-type': { mb: 0 },
            '& a': { color: 'primary.main' },
            '& strong': { fontWeight: 600 },
          }}
        >
          <Markdown>{data.body}</Markdown>
        </Box>
      )}
    </Box>
  )
}

interface CalloutWidgetEditorProps {
  data: CalloutWidgetData | undefined
  onDataChange: (data: CalloutWidgetData) => void
}

export const CalloutWidgetEditor: React.FC<CalloutWidgetEditorProps> = ({ data, onDataChange }) => {
  const current: CalloutWidgetData = data || { title: '', body: '' }

  const update = (partial: Partial<CalloutWidgetData>) => {
    onDataChange({ ...current, ...partial })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="overline" color="text.secondary">Icon</Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {Object.entries(ICON_MAP).map(([key, IconComp]) => {
          const selected = (current.icon || 'info') === key
          return (
            <Tooltip key={key} title={key}>
              <IconButton
                size="small"
                onClick={() => update({ icon: key })}
                sx={{
                  border: '2px solid',
                  borderColor: selected ? 'info.main' : 'grey.300',
                  borderRadius: 1,
                  backgroundColor: selected ? 'info.lighter' : 'transparent',
                }}
              >
                <IconComp fontSize="small" />
              </IconButton>
            </Tooltip>
          )
        })}
      </Box>

      <Typography variant="overline" color="text.secondary">Style</Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {Object.entries(STYLE_COLORS).map(([key, colors]) => {
          const selected = (current.style || 'blue') === key
          return (
            <Box
              key={key}
              onClick={() => update({ style: key })}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                cursor: 'pointer',
                border: '2px solid',
                borderColor: selected ? colors.border : 'transparent',
                backgroundColor: colors.bg,
                transition: 'border-color 0.15s',
              }}
            >
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: colors.border }} />
              <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>{key}</Typography>
            </Box>
          )
        })}
      </Box>

      <TextField
        label="Title"
        size="small"
        fullWidth
        value={current.title}
        onChange={(e) => update({ title: e.target.value })}
      />
      <TextField
        label="Body (supports Markdown)"
        size="small"
        fullWidth
        multiline
        rows={4}
        value={current.body}
        onChange={(e) => update({ body: e.target.value })}
      />

      {current.title && (
        <>
          <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>Preview</Typography>
          <CalloutWidgetRenderer data={current} />
        </>
      )}
    </Box>
  )
}
